import { HeroBriefcaseIcon } from "#components/ui/HeroBriefcaseIcon.tsx";
import { HeroChatBubbleLeftRightIcon } from "#components/ui/HeroChatBubbleLeftRightIcon.tsx";
import { HeroCursorArrowRaysIcon } from "#components/ui/HeroCursorArrowRaysIcon.tsx";
import { HeroDocumentDuplicateIcon } from "#components/ui/HeroDocumentDuplicateIcon.tsx";
import { HeroHeartIcon } from "#components/ui/HeroHeartIcon.tsx";
import { HeroSparklesIcon } from "#components/ui/HeroSparklesIcon.tsx";
import { HeroTrophyIcon } from "#components/ui/HeroTrophyIcon.tsx";
import { HeroUsersIcon } from "#components/ui/HeroUsersIcon.tsx";
import { Markdown } from "#components/ui/markdown/Markdown.tsx";
import { formatCitedContent } from "#features/citations/citedContent.ts";
import { AssistantMessageUser } from "#features/conversations/components/conversation-user/AssistantMessageUser.tsx";
import { MentionToken } from "#features/conversations/components/conversation-input/MentionToken.tsx";
import { useCitedMarkdownComponents } from "#features/conversations/hooks/useCitedMarkdownComponents.tsx";
import type { CitedConversation } from "#features/conversations/hooks/useMessageContent.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import {
    presentCollaborationCategories,
    presentPersonProfileSections,
    type PersonProfileCollaborationCategory,
    type PersonProfileSectionIcon
} from "#types/PersonProfile.ts";
import type { ConversationMessageModel } from "#features/conversations/types/ConversationMessageModel.ts";
import type { PersonProfile, PersonProfileCollaborator, PersonProfileRelatedPerson, PersonProfileSubject } from "#types/types.ts";
import { Avatar, Button, Div, Flex, Text, Tooltip, TooltipTrigger } from "@hopper-ui/components";
import { useCallback, useMemo, useState, type CSSProperties } from "react";
import type { Components } from "react-markdown";
import "#css/ink.css";
import "./AssistantMessagePersonProfile.css";
import "./AssistantMessagePersonProfile.dark.css";

interface Props {
    profile: PersonProfile;
    message: ConversationMessageModel;
    cited: CitedConversation;
    /** The answer's pill numbers by stored citation index (useMessageCitations). */
    citationNumbers?: ReadonlyMap<number, number>;
}

const NO_CITATION_NUMBERS: ReadonlyMap<number, number> = new Map();

// The card's glyphs are the Heroicons solid set, the family the wl-app About card draws from, so the two About cards
// read as one product. Neither Hopper nor @intentui/icons ships them, so they are inlined under components/ui.
const SECTION_ICONS = {
    work: HeroBriefcaseIcon,
    goal: HeroTrophyIcon,
    recognition: HeroHeartIcon,
    people: HeroUsersIcon,
    insight: HeroSparklesIcon
} as const satisfies Record<PersonProfileSectionIcon, typeof HeroBriefcaseIcon>;

// A quiet icon plus a tooltip stands in for the category word, so a collaborator row stays a face and
// a name. Work is shared documents and issues; engagement is Workleap goals and recognition.
const COLLABORATION_ICONS = {
    communication: HeroChatBubbleLeftRightIcon,
    work: HeroDocumentDuplicateIcon,
    engagement: HeroCursorArrowRaysIcon
} as const satisfies Record<PersonProfileCollaborationCategory, typeof HeroChatBubbleLeftRightIcon>;

const COLLABORATION_LABEL_KEYS = {
    communication: "personProfile.collaboration.communication",
    work: "personProfile.collaboration.work",
    engagement: "personProfile.collaboration.engagement"
} as const satisfies Record<PersonProfileCollaborationCategory, string>;

const COLLABORATION_TOOLTIP_KEYS = {
    communication: "personProfile.collaboration.communicationTooltip",
    work: "personProfile.collaboration.workTooltip",
    engagement: "personProfile.collaboration.engagementTooltip"
} as const satisfies Record<PersonProfileCollaborationCategory, string>;

// A long roster reads as a data dump rather than an insight, and past the first handful the names
// stop telling the reader anything. Show a few, keep the rest one press away, and never render more
// than the cap however many the agent found. Same numbers as the profile page.
const COLLABORATORS_COLLAPSED = 5;
const COLLABORATORS_MAX = 10;

// The shared renderer is tuned for the assistant's roomier chat column: body-sm at a 165%
// line-height. Inside a card that reads a size larger than the chat around it, so step both down —
// one notch under body-sm, the same pair the profile card uses. These are the two knobs
// Markdown.css leaves to the host for this.
const PROSE_STYLE = {
    "--wai-prose-font-size": "0.8125rem",
    "--wai-prose-line-height": "1.5"
} as CSSProperties;

const ProfileProse = ({ content, components }: { content: string; components: Components }) => {
    const formatted = useMemo(() => formatCitedContent(content), [content]);

    return (
        <Div UNSAFE_fontSize="0.8125rem" style={PROSE_STYLE}>
            <Markdown content={formatted} components={components} />
        </Div>
    );
};

// "January 2020" in the reader's locale. The day is noise for a hire date, and a bare ISO string
// would be the one untranslated thing on the card.
//
// Formatted in UTC because that is how a date-only value parses: `new Date("2020-01-01")` is UTC
// midnight, so rendering it in a zone west of UTC walks it back into the previous month and the
// card states a hire month the directory never gave.
const formatJoined = (hireDate: string, locale: string): string | undefined => {
    const parsed = new Date(hireDate);

    if (Number.isNaN(parsed.getTime())) {
        return undefined;
    }

    return new Intl.DateTimeFormat(locale, { month: "long", year: "numeric", timeZone: "UTC" }).format(parsed);
};

// The hover card is looked up by email, so a name the directory gave no email for stays plain text.
const RelatedPerson = ({ person, conversationId, disabled }: {
    person: PersonProfileRelatedPerson;
    conversationId: string;
    disabled: boolean;
}) => {
    if (!person.email) {
        return <>{person.name}</>;
    }

    return (
        <AssistantMessageUser
            href={`mailto:${person.email}`}
            userName={person.name}
            email={person.email}
            disabled={disabled}
            conversationId={conversationId}
        />
    );
};

// A count alone ("1 direct report") tells the reader nothing they can act on. Naming the reports
// does, and past a few the names stop earning their line — so name the first handful and count the
// remainder.
const NAMED_DIRECT_REPORTS = 3;

const DirectReports = ({ subject, conversationId, disabled }: {
    subject: PersonProfileSubject;
    conversationId: string;
    disabled: boolean;
}) => {
    const { tShared } = useSharedTranslation();
    const people = subject.directReports.filter(person => person.name);
    // Names outrank a conflicting count: a stale zero would otherwise drop reports we can see.
    const total = Math.max(subject.directReportsCount ?? 0, people.length);

    if (total === 0) {
        return null;
    }

    // Nothing to name, so the count is all there is to say.
    if (people.length === 0) {
        return <Text size="xs" color="neutral-weak">{tShared("personProfile.directReports", { count: total })}</Text>;
    }

    const shown = people.slice(0, NAMED_DIRECT_REPORTS);
    const remaining = Math.max(total - shown.length, 0);

    return (
        <Text size="xs" color="neutral-weak">
            {tShared("personProfile.managesLabel")}
            {" "}
            {shown.map((person, index) => (
                <span key={person.email ?? `${index}-${person.name}`}>
                    {index > 0 ? ", " : ""}
                    <RelatedPerson person={person} conversationId={conversationId} disabled={disabled} />
                </span>
            ))}
            {remaining > 0 ? ` ${tShared("personProfile.andOthers", { count: remaining })}` : ""}
        </Text>
    );
};

// The profile page omits these because its own widgets carry them; a conversation has no page
// around the card, so the reader would have nowhere to look up the role and reporting line.
const SubjectFacts = ({ subject, conversationId, disabled }: {
    subject: PersonProfileSubject;
    conversationId: string;
    disabled: boolean;
}) => {
    const { tShared, i18n } = useSharedTranslation();

    const role = [subject.jobTitle, subject.department].filter(Boolean).join(" · ");
    const joined = subject.hireDate ? formatJoined(subject.hireDate, i18n.language) : undefined;
    const place = [subject.location, subject.workLocation].filter(Boolean).join(" · ");

    const standing = [
        joined ? tShared("personProfile.joined", { date: joined }) : undefined,
        place || undefined
    ].filter(Boolean).join(" · ");

    const hasReports = Math.max(subject.directReportsCount ?? 0, subject.directReports.length) > 0;

    if (!role && !subject.manager && !standing && !hasReports) {
        return null;
    }

    return (
        <Flex direction="column" gap="stack-xs">
            {role && <Text size="sm" color="neutral-weak">{role}</Text>}
            {subject.manager &&
                <Text size="xs" color="neutral-weak">
                    {tShared("personProfile.reportsToLabel")}
                    {" "}
                    <RelatedPerson person={subject.manager} conversationId={conversationId} disabled={disabled} />
                </Text>
            }
            <DirectReports subject={subject} conversationId={conversationId} disabled={disabled} />
            {standing && <Text size="xs" color="neutral-weakest">{standing}</Text>}
        </Flex>
    );
};

const CollaborationCategoryIcon = ({ category }: { category: PersonProfileCollaborationCategory }) => {
    const { tShared } = useSharedTranslation();
    const Icon = COLLABORATION_ICONS[category];

    return (
        <TooltipTrigger>
            <Button
                variant="ghost-secondary"
                size="xs"
                height="auto"
                padding="core_0"
                type="button"
                aria-label={tShared(COLLABORATION_LABEL_KEYS[category])}
            >
                <Icon size="sm" color="neutral-weak" />
            </Button>
            <Tooltip>{tShared(COLLABORATION_TOOLTIP_KEYS[category])}</Tooltip>
        </TooltipTrigger>
    );
};

const CollaboratorRow = ({ person }: { person: PersonProfileCollaborator }) => {
    const categories = presentCollaborationCategories(person);

    return (
        <Flex alignItems="center" gap="inline-sm">
            {person.memberId
                // A mention chip, as every other person in the conversation is, opening the same card by member id.
                ? <Text size="sm" color="neutral"><MentionToken name={person.name} imageUrl={person.imageUrl} card={{ memberId: person.memberId, isPressable: true }} /></Text>
                : (
                    <>
                        {/* No photo means initials, never a guess: Core only resolves one from a member id. */}
                        <Avatar data-private name={person.name} src={person.imageUrl ?? undefined} size="xs" flexShrink={0} />
                        <Text size="sm" color="neutral">{person.name}</Text>
                    </>
                )}
            {categories.length > 0 &&
                <Flex alignItems="center" gap="inline-xs">
                    {categories.map(category => <CollaborationCategoryIcon key={category} category={category} />)}
                </Flex>
            }
        </Flex>
    );
};

const Collaborators = ({ people }: { people: PersonProfileCollaborator[] }) => {
    const { tShared } = useSharedTranslation();
    const [isExpanded, setIsExpanded] = useState(false);

    const onToggle = useCallback(() => setIsExpanded(current => !current), []);

    const capped = people.slice(0, COLLABORATORS_MAX);
    const visible = isExpanded ? capped : capped.slice(0, COLLABORATORS_COLLAPSED);
    const hiddenCount = capped.length - COLLABORATORS_COLLAPSED;

    return (
        <Flex direction="column" gap="inline-sm" alignItems="flex-start">
            {visible.map((person, index) => (
                <CollaboratorRow key={person.memberId ?? `${index}-${person.name}`} person={person} />
            ))}
            {hiddenCount > 0 &&
                <Button variant="ghost-secondary" size="xs" type="button" aria-expanded={isExpanded} onPress={onToggle}>
                    {isExpanded
                        ? tShared("personProfile.collaboratorsShowLess")
                        : tShared("personProfile.collaboratorsShowMore", { count: hiddenCount })}
                </Button>
            }
        </Flex>
    );
};

export const AssistantMessagePersonProfile = ({ profile: { displayName, intro, sections, subject }, message, cited, citationNumbers = NO_CITATION_NUMBERS }: Props) => {
    const { tShared } = useSharedTranslation();
    const rows = useMemo(() => presentPersonProfileSections(sections), [sections]);
    const components = useCitedMarkdownComponents(cited, message, citationNumbers);

    return (
        <Flex
            className="wai-person-profile"
            direction="column"
            gap="inline-md"
            padding="inset-md"
        >
            <Flex alignItems="center" gap="inline-md">
                <Avatar
                    data-private
                    name={displayName}
                    src={subject?.imageUrl ?? undefined}
                    size="2xl"
                    flexShrink={0}
                />
                <Flex direction="column" gap="stack-xs" minWidth="core_0">
                    <Text size="lg" fontWeight="heading-xs" color="neutral">
                        {displayName}
                    </Text>
                    {subject &&
                        <SubjectFacts
                            subject={subject}
                            conversationId={cited.conversationId}
                            disabled={message.assistantStatus !== "idle"}
                        />
                    }
                </Flex>
            </Flex>
            <ProfileProse content={intro} components={components} />
            {rows.map(row => {
                const Icon = SECTION_ICONS[row.icon];
                const heading = row.titleKey ? tShared(row.titleKey) : row.title;

                return (
                    <Flex key={row.key} direction="column" gap="inline-xs">
                        <Flex alignItems="center" gap="inline-sm">
                            {/* Same weight as the label beside it. Left at full strength the solid
                                glyph reads as a dark blob against a neutral-weak heading. */}
                            <Icon size="sm" color="neutral-weak" aria-hidden="true" />
                            <Text
                                size="xs"
                                fontFamily="overline"
                                textTransform="uppercase"
                                color="neutral-weak"
                            >
                                {heading}
                            </Text>
                        </Flex>
                        {row.body
                            ? <ProfileProse content={row.body} components={components} />
                            : null}
                        {row.people.length > 0
                            ? <Collaborators people={row.people} />
                            : null}
                    </Flex>
                );
            })}
        </Flex>
    );
};
